<script setup lang="ts">
import { onMounted } from 'vue'
import { usePartnerStore } from '@/stores/usePartnerStore'
import { storeToRefs } from 'pinia'
import SaveButton from '@/components/common/buttons/SaveButton.vue'
import Select from '@/components/common/selects/SelectSimple.vue'
import NumberInput from '@/components/common/inputs/NumberInput.vue'
import DateInput from '@/components/common/inputs/DateInput.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import { useToast } from 'vue-toast-notification'

const props = defineProps<{ partnerId: number }>()
const $toast = useToast()

const partnerStore = usePartnerStore()
const { loading, env } = storeToRefs(partnerStore)

const competitors = [
  { id: 'orange', value: 'orange', label: 'ORANGE' },
  { id: 'sfr', value: 'sfr', label: 'SFR' },
  { id: 'bte', value: 'bte', label: 'BTE' },
  { id: 'free', value: 'free', label: 'FREE' },
  { id: 'linkt', value: 'linkt', label: 'LINKT' },
  { id: 'celeste', value: 'celeste', label: 'CELESTE' },
  { id: 'adista', value: 'adista', label: 'ADISTA' },
  { id: 'marque_blanche', value: 'marque_blanche', label: 'MARQUE BLANCHE' },
  { id: 'autre', value: 'autre', label: 'AUTRE' },
  { id: 'null', value: 'false', label: 'AUCUN' },
]

const technologies = [
  { id: 'adsl', value: 'adsl', label: 'ADSL' },
  { id: 'vdsl', value: 'vdsl', label: 'VDSL' },
  { id: 'ftth', value: 'ftth', label: 'FTTH' },
  { id: 'ftte', value: 'ftte', label: 'FTTE' },
  { id: 'ftto', value: 'ftto', label: 'FTTO' },
  { id: 'fh', value: 'fh', label: 'FH' },
  { id: '4g_5g', value: '4g_5g', label: '4G/5G' },
  { id: 'satelite', value: 'satelite', label: 'SATELITE' },
  { id: 'autre', value: 'autre', label: 'AUTRE' },
  { id: 'null', value: 'false', label: 'AUCUNE' },
]

function isCompleted(envName: string) {
  if (!env.value) return false
  switch (envName) {
    case 'Mobile':
      return !!(
        env.value.operatorMobileDate &&
        env.value.operatorMobileNbline &&
        env.value.operatorMobileCompetitorName
      )
    case 'Fixe':
      return !!(
        env.value.operatorFixeDate &&
        env.value.operatorFixeNbline &&
        env.value.operatorFixeCompetitorName
      )
    case 'Data':
      return !!(
        env.value.operatorDataDate &&
        env.value.operatorDataTechnology &&
        env.value.operatorDataCompetitorName
      )
    case 'Intégrateur':
      return !!(
        env.value.integratorDate &&
        env.value.integratorNbchannel &&
        env.value.integratorCompetitorName
      )
    default:
      return false
  }
}

const saveEnv = async () => {
  try {
    await partnerStore.updateEnvByPartnerId(props.partnerId, JSON.parse(JSON.stringify(env.value)))
    $toast.success('Les modifications ont été enregistrées avec succès.')
  } catch (error) {
    $toast.error('Une erreur est survenue lors de la sauvegarde des modifications.')
    partnerStore.getProspectEnvById(props.partnerId)
  }
}

onMounted(async () => {
  partnerStore.getProspectEnvById(props.partnerId)
})
</script>

<template>
  <div id="env" class="flex flex-col gap-7 p-8 w-[400px] min-w-[380px] bg-base-100 rounded-2xl">
    <div class="bg-background text-primary p-1 text-center font-bold rounded-full">
      Environnement Prospect
    </div>
    <div v-if="env">
      <div role="tablist" class="tabs tabs-box flex justify-center shadow-none">
        <label class="tab indicator">
          <input type="radio" name="tabs_env" checked />
          <div
            :class="{
              'indicator-item status': true,
              'status-error': !isCompleted('Mobile'),
              'status-success': isCompleted('Mobile'),
            }"
          ></div>
          Mobile
        </label>
        <div role="tabpanel" class="tab-content bg-base-100 border-none shadow-none py-5 px-2">
          <div class="grid grid-cols-12 gap-2">
            <DateInput
              class="col-span-6"
              label="Fin d'engagement :"
              v-model="env.operatorMobileDate"
            />
            <NumberInput
              class="col-span-2"
              label="Nb lignes :"
              v-model="env.operatorMobileNbline"
            />
            <Select
              class="col-span-7"
              label="Concurrent :"
              :options="competitors"
              v-model="env.operatorMobileCompetitorName"
            />
          </div>
        </div>
        <label class="tab indicator">
          <input type="radio" name="tabs_env" role="tab" />
          <div
            :class="{
              'indicator-item status': true,
              'status-error': !isCompleted('Fixe'),
              'status-success': isCompleted('Fixe'),
            }"
          ></div>
          Fixe
        </label>
        <div role="tabpanel" class="tab-content bg-base-100 border-none shadow-none py-5 px-2">
          <div class="grid grid-cols-12 gap-2">
            <DateInput class="col-span-6" label="Fin d'engagement" v-model="env.operatorFixeDate" />
            <NumberInput class="col-span-2" label="Nb lignes :" v-model="env.operatorFixeNbline" />
            <Select
              class="col-span-7"
              label="Concurrent :"
              :options="competitors"
              v-model="env.operatorFixeCompetitorName"
            />
          </div>
        </div>

        <label class="tab indicator">
          <input type="radio" name="tabs_env" role="tab" />
          <div
            :class="{
              'indicator-item status': true,
              'status-error': !isCompleted('Data'),
              'status-success': isCompleted('Data'),
            }"
          ></div>
          Data
        </label>
        <div role="tabpanel" class="tab-content bg-base-100 border-none shadow-none py-5 px-2">
          <div class="grid grid-cols-12 gap-2">
            <DateInput
              class="col-span-6"
              label="Fin d'engagement :"
              v-model="env.operatorDataDate"
            />
            <Select
              class="col-span-4"
              label="Technologie :"
              :options="technologies"
              v-model="env.operatorDataTechnology"
            />
            <Select
              class="col-span-7"
              label="Concurrent :"
              :options="competitors"
              v-model="env.operatorDataCompetitorName"
            />
          </div>
        </div>

        <label class="tab indicator">
          <input type="radio" name="tabs_env" role="tab" />
          <div
            :class="{
              'indicator-item  status': true,
              'status-error': !isCompleted('Intégrateur'),
              'status-success': isCompleted('Intégrateur'),
            }"
          ></div>
          Intégrateur
        </label>
        <div role="tabpanel" class="tab-content bg-base-100 border-none shadow-none py-5 px-2">
          <div class="grid grid-cols-12 gap-2">
            <DateInput class="col-span-6" label="Fin d'engagement :" v-model="env.integratorDate" />
            <NumberInput class="col-span-2" label="Nb canaux :" v-model="env.integratorNbchannel" />
            <TextInput
              class="col-span-7"
              label="Concurrent"
              v-model="env.integratorCompetitorName"
              placeholder="Entrez le nom du concurrent"
            />
          </div>
        </div>
      </div>
      <SaveButton :loading="loading" @save="saveEnv" />
    </div>
    <div v-else class="skeleton opacity-30 m-4 h-[330px]"></div>
  </div>
</template>

<style>
#env {
  .tabs-box {
    padding: 0;
    background-color: #fdfdfd;
    .tab-active {
      background-color: var(--color-primary) !important;
      color: var(--color-primary-content) !important;
    }
    > :is(label:has(:checked)) {
      background-color: var(--color-primary);
      color: var(--color-primary-content);
    }
    .tab {
      background-color: var(--color-base-300);
      border-radius: 0;
      width: fit-content;
    }

    .tab:first-child {
      border-radius: 0.5rem 0 0 0.5rem;
    }
    .tab:nth-last-child(2) {
      border-radius: 0 0.5rem 0.5rem 0;
    }
    .tab-content {
      margin-top: 0.5rem;
    }
  }

  .indicator-item {
    right: 0.5rem;
    top: 0.5rem;
  }
}
</style>
